import {type PropsWithChildren, useCallback, useEffect} from 'react'
import {ConnectionsContext} from './ConnectionsContext'
import {api} from '../../trpc/api.ts'
import type {Connection, ConnectionId, GitHubRepository} from '@package/shared'
import type {
  AddConnectionRequest,
  UpdateConnectionRequest,
  UpdateGitHubRepositoryRequest,
} from '@app/api-resolver/router'
import {useArrayState} from '@mstuercke/react-utils/hooks'
import {showNotification} from '../../notifications/showNotification.ts'

export const ConnectionsProvider = ({children}: PropsWithChildren) => {
  const {data, isLoading: loading, refetch} = api.connections.list.useQuery()
  const [connections, setConnections, {appendItem, replaceItem, removeItemById}] = useArrayState<Connection>('id')
  useEffect(() => {
    if (!data) return
    setConnections(data)
  }, [data, setConnections])

  // refresh recommendations
  const {mutateAsync: refreshRecommendations, isPending: refreshingRecommendations} =
    api.connections.refreshRecommendations.useMutation(
      showNotification('Recommendations refreshed successfully', 'Error while refreshing recommendation'),
    )
  const handleRefreshRecommendations = useCallback(
    async (connectionId: ConnectionId) => {
      const connection = await refreshRecommendations({id: connectionId})
      replaceItem(connection)
    },
    [refreshRecommendations, replaceItem],
  )

  // add
  const {mutateAsync: addConnection, isPending: addingConnection} = api.connections.add.useMutation(
    showNotification('Connection added successfully', 'Error while adding connection'),
  )
  const handleAdd = useCallback(
    async (connection: AddConnectionRequest) => {
      const addedConnection = await addConnection(connection)
      appendItem(addedConnection)
      return addedConnection
    },
    [addConnection, appendItem],
  )

  // update
  const {mutateAsync: updateConnection, isPending: updatingConnection} = api.connections.update.useMutation(
    showNotification('Connection updated successfully', 'Error while updating connection'),
  )
  const handleUpdate = useCallback(
    async (connection: UpdateConnectionRequest) => {
      const updatedConnection = await updateConnection(connection)
      replaceItem(updatedConnection)
      return updatedConnection
    },
    [replaceItem, updateConnection],
  )

  // delete
  const {mutateAsync: deleteConnection, isPending: deletingConnection} = api.connections.remove.useMutation(
    showNotification('Connection deleted successfully', 'Error while deleting connection'),
  )
  const handleDelete = useCallback(
    async (connectionId: ConnectionId) => {
      await deleteConnection({id: connectionId})
      removeItemById(connectionId)
    },
    [deleteConnection, removeItemById],
  )

  // update repository
  const {mutateAsync: updateRepository, isPending: updatingRepository} = api.gitHubRepositories.update.useMutation(
    showNotification('Repository updated successfully', 'Error while updating repository'),
  )
  const handleUpdateRepository = useCallback(
    async (request: UpdateGitHubRepositoryRequest) => {
      const updated = await updateRepository(request)
      replaceItem(updated)
      return updated
    },
    [replaceItem, updateRepository],
  )

  // disable repository
  const {mutateAsync: disableRepository, isPending: disablingRepository} = api.gitHubRepositories.disable.useMutation(
    showNotification('Repository disabled successfully', 'Error while disabling repository'),
  )
  const handleDisableRepository = useCallback(
    async (connectionId: ConnectionId, id: GitHubRepository['id']) => {
      const archived = await disableRepository({connectionId, id})
      replaceItem(archived)
      return archived
    },
    [disableRepository, replaceItem],
  )

  // enable repository
  const {mutateAsync: enableRepository, isPending: enablingRepository} = api.gitHubRepositories.enable.useMutation(
    showNotification('Repository enabled successfully', 'Error while enabling repository'),
  )
  const handleEnableRepository = useCallback(
    async (connectionId: ConnectionId, id: GitHubRepository['id']) => {
      const unarchived = await enableRepository({connectionId, id})
      replaceItem(unarchived)
      return unarchived
    },
    [replaceItem, enableRepository],
  )

  return (
    <ConnectionsContext.Provider
      value={{
        connections,
        refetch: useCallback(async () => void (await refetch()), [refetch]),
        loading,

        addConnection: handleAdd,
        updateConnection: handleUpdate,
        deleteConnection: handleDelete,
        updateRepository: handleUpdateRepository,
        disableRepository: handleDisableRepository,
        enableRepository: handleEnableRepository,
        saving:
          addingConnection ||
          updatingConnection ||
          deletingConnection ||
          updatingRepository ||
          disablingRepository ||
          enablingRepository,

        refreshRecommendations: handleRefreshRecommendations,
        refreshingRecommendations,
      }}
    >
      {children}
    </ConnectionsContext.Provider>
  )
}
